import React, { useMemo, useState } from 'react';
import packageJson from '../../../package.json';
import Icon from '../Common/Icon';
import CollapsibleSection from '../UI/CollapsibleSection';
import ScrollReveal, { StaggerGroup } from '../UI/ScrollReveal';
import '../../styles/pages.css';
import '../../styles/publicEditorialLock.css';
import PublicPageHeader from './PublicPageHeader';
import PublicPageFooter from './PublicPageFooter';
import {
  CORE_LOOP_GROUPS,
  HELP_TASKS,
  HELP_TRUST_FACTS,
  PLATFORM_GUIDANCE
} from '../../content/helpGuidanceContent';

const CURRENT_RELEASE_VERSION = packageJson.version;

const AGENT_RESOURCES = [
  { label: 'sitemap.md', href: '/sitemap.md', summary: 'Markdown map of public pages, app surfaces, and agent-readable endpoints.' },
  { label: 'llms.txt', href: '/llms.txt', summary: 'Compact LLM entry point with product context and canonical links.' },
  { label: 'skills.md', href: '/skills.md', summary: 'Agent task map for ThryveLoop workflows, privacy, sync, and support surfaces.' },
  { label: 'rss.xml', href: '/rss.xml', summary: 'Machine-readable updates for release, help, and agent resource changes.' }
];

const RESOURCE_LIBRARY = [
  {
    id: 'agent-sitemap',
    type: 'Agent',
    date: '2026-06-23',
    title: 'Agent sitemap',
    summary: 'A concise map of landing, help, science, privacy, terms, downloads, and app concepts.',
    href: '/sitemap.md',
    tags: ['agents', 'sitemap', 'public pages'],
    links: ['agent-llms', 'agent-skills', 'public-help']
  },
  {
    id: 'agent-llms',
    type: 'Agent',
    date: '2026-06-23',
    title: 'llms.txt',
    summary: 'LLM-friendly product summary, canonical routes, safety notes, and support paths.',
    href: '/llms.txt',
    tags: ['agents', 'llms', 'context'],
    links: ['agent-sitemap', 'agent-skills', 'science']
  },
  {
    id: 'agent-skills',
    type: 'Agent',
    date: '2026-06-23',
    title: 'skills.md',
    summary: 'Task-oriented guide for agents helping users with loops, sync, privacy, and AI Coach.',
    href: '/skills.md',
    tags: ['agents', 'skills', 'support'],
    links: ['agent-sitemap', 'agent-llms', 'public-help']
  },
  {
    id: 'rss',
    type: 'Feed',
    date: '2026-06-23',
    title: 'RSS feed',
    summary: 'Release and resource feed for readers, crawlers, and agent monitors.',
    href: '/rss.xml',
    tags: ['rss', 'feed', 'updates'],
    links: ['agent-sitemap', 'downloads', 'public-help']
  },
  {
    id: 'public-help',
    type: 'Guide',
    date: '2026-07-14',
    title: 'Help & User Guidance',
    summary: 'Current release notes, sync guidance, plan explanations, AI Coach notes, and FAQs.',
    href: '?page=help',
    tags: ['help', 'release', 'sync'],
    links: ['agent-skills', 'privacy', 'downloads']
  },
  {
    id: 'science',
    type: 'Research',
    date: '2026-06-23',
    title: 'Science backing',
    summary: 'Behavior design, self-experimentation, evidence quality, and privacy-first learning.',
    href: '?page=science',
    tags: ['science', 'evidence', 'experiments'],
    links: ['references', 'agent-llms', 'public-help']
  },
  {
    id: 'references',
    type: 'Research',
    date: '2026-06-23',
    title: 'Research references',
    summary: 'Bibliography for the behavior science and product principles behind ThryveLoop.',
    href: '?page=references',
    tags: ['references', 'bibliography'],
    links: ['science', 'agent-sitemap']
  },
  {
    id: 'downloads',
    type: 'Download',
    date: '2026-06-23',
    title: 'Desktop downloads',
    summary: 'Latest desktop manifest and offline-first Lifetime app download.',
    href: '/downloads/',
    tags: ['desktop', 'downloads', 'lifetime'],
    links: ['rss', 'privacy', 'public-help']
  },
  {
    id: 'privacy',
    type: 'Policy',
    date: '2026-06-23',
    title: 'Privacy promise',
    summary: 'Data ownership, encryption, local-first controls, exports, and offline behavior.',
    href: '?page=privacy',
    tags: ['privacy', 'encryption', 'ownership'],
    links: ['terms', 'downloads', 'agent-llms']
  },
  {
    id: 'terms',
    type: 'Policy',
    date: '2026-06-23',
    title: 'Terms of service',
    summary: 'Plan terms, acceptable use, billing surfaces, desktop usage, and support boundaries.',
    href: '?page=terms',
    tags: ['terms', 'billing', 'plans'],
    links: ['privacy', 'public-help']
  }
];

const RESOURCE_TYPES = ['All', ...Array.from(new Set(RESOURCE_LIBRARY.map((item) => item.type)))];

const HelpPage = ({ onBack, onNavigateHome, onNavigatePage, onStartApp, activePage = 'help' }) => {
  const handleHomeClick = () => {
    onNavigateHome ? onNavigateHome() : onBack?.();
  };

  const handleStartApp = () => {
    onStartApp?.();
  };

  const [resourceQuery, setResourceQuery] = useState('');
  const [resourceType, setResourceType] = useState('All');
  const [resourceSort, setResourceSort] = useState('newest');
  const [resourceView, setResourceView] = useState(() => {
    if (typeof window === 'undefined') return 'list';
    return new URLSearchParams(window.location.search || '').get('view') === 'graph' ? 'graph' : 'list';
  });
  const [selectedResourceId, setSelectedResourceId] = useState(null);
  const [helpQuery, setHelpQuery] = useState('');

  const filteredResources = useMemo(() => {
    const query = resourceQuery.trim().toLowerCase();
    const rows = RESOURCE_LIBRARY.filter((item) => {
      const matchesType = resourceType === 'All' || item.type === resourceType;
      const haystack = [
        item.title,
        item.summary,
        item.type,
        ...(item.tags || [])
      ].join(' ').toLowerCase();
      return matchesType && (!query || haystack.includes(query));
    });

    return rows.sort((a, b) => {
      if (resourceSort === 'oldest') return a.date.localeCompare(b.date);
      if (resourceSort === 'title') return a.title.localeCompare(b.title);
      return b.date.localeCompare(a.date);
    });
  }, [resourceQuery, resourceSort, resourceType]);

  const selectedResource = filteredResources.find((item) => item.id === selectedResourceId) || null;
  const graphResources = selectedResource
    ? [
        selectedResource,
        ...filteredResources.filter((item) => (
          item.id !== selectedResource.id
          && ((selectedResource.links || []).includes(item.id) || (item.links || []).includes(selectedResource.id))
        ))
      ].slice(0, 10)
    : filteredResources.slice(0, 10);

  const graphNodePosition = (index, total) => {
    if (index === 0 || total <= 1) return { x: 50, y: 50 };
    const outerNodeCount = Math.max(total - 1, 1);
    const angle = (-Math.PI / 2) + ((Math.PI * 2 * (index - 1)) / outerNodeCount);
    const radius = index % 2 === 0 ? 38 : 33;
    return {
      x: 50 + Math.cos(angle) * radius,
      y: 50 + Math.sin(angle) * radius
    };
  };

  const graphActiveResource = selectedResource || graphResources[0] || null;
  const graphRelatedResources = graphActiveResource
    ? filteredResources.filter((item) => (
        item.id !== graphActiveResource.id
        && ((graphActiveResource.links || []).includes(item.id) || (item.links || []).includes(graphActiveResource.id))
      ))
    : [];

  const resetResourceFilters = () => {
    setResourceQuery('');
    setResourceType('All');
    setResourceSort('newest');
    setSelectedResourceId(null);
  };

  const isResourcesPage = activePage === 'resources';
  const pageHeading = isResourcesPage ? 'Resources' : 'Help & User Guidance';
  const pageSubtitle = isResourcesPage
    ? 'Search product guidance, release notes, public policies, research, downloads, and agent-ready files.'
    : 'Find the next step for Web, mobile web, Desktop Lifetime, encrypted sync, AI Coach, plans, and recovery.';

  const faqSections = [
    {
      title: 'Safe Web & Desktop Setup',
      icon: <Icon name="key" size={18} strokeWidth={2} />,
      items: [
        {
          q: 'Check Settings before you rely on sync',
          summary: 'Confirm encrypted sync, plan status, and desktop controls.',
          a: 'Open Settings after sign-in. Confirm your account and plan, then open Cloud Sync & Encryption. Review sync status, queue counts, and snapshot source. On Desktop Lifetime, choose the datasets you want to sync and use Sync now.'
        },
        {
          q: 'Remember the sync key on a trusted device',
          summary: 'Keep a trusted browser ready to open encrypted records.',
          a: 'Confirm Remember sync key on this device in Settings. Remembered Web keys use a rolling 30-day device window. Use this only on a device and browser profile you control.'
        },
        {
          q: 'Understand the zero-knowledge limit',
          summary: 'The server cannot recover a lost encryption key.',
          a: 'ThryveLoop encrypts personal records on your device before sync, so the server stores ciphertext. If every browser loses or replaces the old key and you do not know the previous password, the server cannot recover that key. The encrypted records remain stored but cannot be opened.'
        },
        {
          q: 'Move to another browser or device safely',
          summary: 'Verify sync and export before removing local access.',
          a: 'Check that pending queue counts are zero, export a current backup, and keep one working browser until the new device opens the records. After a password reset, first use a browser that previously opened the records. Use Previous password on the unlock screen if its remembered key was replaced and you still know that password.'
        },
        {
          q: 'Treat desktop sync as an explicit action',
          summary: 'Desktop Lifetime saves locally first.',
          a: 'Desktop work remains local until the selected datasets sync. A signed-in session does not confirm that every change reached the cloud. Use Sync now and check the Sync Integrity Center before reinstalling, changing devices, or removing local data.'
        }
      ]
    },
    {
      title: 'Start',
      icon: <Icon name="rocket" size={18} strokeWidth={2} />,
      items: [
        {
          q: 'Use the core loop',
          summary: 'Move from what matters now to one useful next decision.',
          a: 'Orient around what matters, Observe what is happening, Choose a useful outcome, Test one small action, Notice the signal, Reflect on the evidence, Decide what to keep or change, and Integrate the learning into your next loop.'
        },
        {
          q: 'Start with one moment',
          summary: 'Log energy, emotion, context, and one sentence about what happened.',
          a: 'Begin with one observation. Add the situation, the energy level, and the context around it. Create a tiny test when you see a pattern worth checking.'
        },
	        {
	          q: 'Protect your private data',
	          summary: 'Your account controls access, plan status, and encrypted sync.',
	          a: HELP_TRUST_FACTS.encryption
	        },
	        {
	          q: 'Continue AI Coach across browsers',
	          summary: 'Coach history follows your signed-in account.',
	          a: 'AI Coach keeps conversation history encrypted and scoped to the signed-in account. Another trusted browser can load the latest encrypted thread after it has the correct sync key.'
	        }
	      ]
	    },
    {
      title: 'Use the Loop',
      icon: <Icon name="repeat" size={18} strokeWidth={2} />,
      items: [
        {
          q: 'Move through Observe, Test, Decide, Results',
          summary: 'Each area has one job, so the loop stays clear.',
          a: 'Observe captures signals. Test turns a question into an action. Decide closes the loop. Results shows the strongest signal and the next useful move.'
        },
        {
          q: 'Run a useful test window',
          summary: 'Most personal tests work best across 2-4 weeks.',
          a: 'Choose a window long enough to see a pattern and short enough to remember the context. Two to four weeks works for most routines.'
        },
        {
          q: 'Use low-signal results well',
          summary: 'A test can fail and still teach you what to change.',
          a: 'Use Decide to keep, adjust, pause, or close the test. Record what happened so the next loop starts with better context.'
        }
      ]
    },
    {
      title: 'Sync & Privacy',
      icon: <Icon name="shield-check" size={18} strokeWidth={2} />,
      items: [
        {
          q: 'Understand encrypted sync',
          summary: 'Records are encrypted on your device before cloud sync.',
          a: 'ThryveLoop stores encrypted copies so your account can work across devices. The app keeps the readable content on your device.'
        },
        {
          q: 'Check delayed sync',
          summary: 'Open Settings → Cloud Sync & Encryption.',
          a: 'Use Sync now when changes are pending. Then check the Sync Integrity Center for queue age, snapshot source, failed operations, and repair options.'
        },
        {
          q: 'Export your data',
          summary: 'Use Settings → Export Data for a full copy.',
          a: 'Use Settings for a full export. Use Journal entries when you only need selected journal records.'
        }
      ]
    },
    {
      title: 'Plans',
      icon: <Icon name="credit-card" size={18} strokeWidth={2} />,
      items: [
        {
          q: 'Choose a plan',
          summary: 'Start free. Upgrade for guided insight, higher limits, or desktop.',
          a: 'Explorer covers the core loop. Believer adds guided insight. Guardian adds higher AI limits and Results. Lifetime adds the desktop app with offline-first control and explicit manual sync.'
        },
        {
          q: 'Use AI as an option',
          summary: 'The core loop works without AI.',
          a: 'Observe, Test, Decide, search, exports, and core analytics work without AI. Enable AI only when you want guided reflection or summaries.'
        },
	        {
	          q: 'Manage billing',
	          summary: 'Use Settings → License & Subscription.',
	          a: 'Verify payments, open billing, and review your plan from Settings. ThryveLoop checks PocketBase for your active Lemon Squeezy purchases and subscriptions. Cancelled Believer/Guardian subscriptions stay active until the paid-through date. Refunds remove only the refunded purchase. If something looks delayed, run the in-app sync action and contact support with the purchase email.'
	        }
      ]
    },
    {
      title: 'Journal AI Help',
      icon: <Icon name="pen" size={18} strokeWidth={2} />,
      items: [
        {
          q: 'Write manually on any plan',
          summary: 'Manual Journal stays separate from paid AI helpers.',
          a: 'Use Journal to write, link reflections to experiments, import entries, search, filter, and export without turning on AI.'
        },
        {
          q: 'Use the three AI helpers',
          summary: 'Reflection Prompt, Analyze My Journal, and Build the Habit each have one job.',
          a: 'Get Reflection Prompt creates one short question. Analyze My Journal scans recent, older, and imported entries for themes. Build the Habit uses normalized journal history, frequency, focus areas, observations, and experiments to suggest small next actions.'
        },
        {
          q: 'Understand AI calls and saved outputs',
          summary: 'Paid AI helpers use your shared daily AI allowance.',
          a: 'When your model is enabled, each Journal AI helper uses one AI call. Saved Journal AI outputs sync as encrypted account payloads; preferences keep only safe summary metadata.'
        }
      ]
    },
    {
      title: 'AI Coach',
      icon: <Icon name="bot" size={18} strokeWidth={2} />,
      items: [
        {
          q: 'Ask natural life and project questions',
          summary: 'You do not need to know which app record is relevant.',
          a: 'Ask about energy, projects, work, decisions, motivation, journals, experiments, or what to try next. AI Coach routes the question and connects it to relevant app evidence when that evidence exists.'
        },
        {
          q: 'Understand the evidence ladder',
          summary: 'Open Coach combines relevant personal context with broader coaching and domain knowledge.',
          a: 'Open Coach answers the life issue first with useful principles, best practices, trade-offs, alternatives, and uncertainty. It uses personal records only when they materially improve the answer. Recent activity and statistics stay out of the response when they are not relevant, and personal evidence remains distinct from broader coaching knowledge.'
        },
        {
          q: 'Use partial evidence well',
          summary: 'The Coach should still help without making technical excuses.',
          a: 'When evidence is partial, it still helps by using the best available context, adding a gentle caveat, and suggesting one useful next action. It should not pretend to have proof it does not have.'
        },
        {
          q: 'Control what a Coach conversation can use',
          summary: 'Choose exact categories; changing account, provider, conversation, or categories asks again.',
          a: 'AI Coach selects a bounded set of relevant records from the categories you approve and keeps personal evidence distinct from inference. It cannot expand that selection, browse account storage, use coding tools, or change records without a separate explicit approval. Desktop local models use the same category controls without sending local-only data to a cloud model.'
        },
        {
          q: 'Understand the context budget',
          summary: 'The model window includes instructions, selected context, conversation, attachments, tools, and the reply.',
          a: 'The meter estimates total input and keeps a separate reply reserve. A 4,096 value is not a universal Coach cap: it may be the configured model window, while larger models can reserve up to 4,096 reply tokens. Long threads retain recent wording, relevant older turns, user corrections, and decisions before compacting less useful material.'
        },
        {
          q: 'Use Guided Coaching',
          summary: 'Choose Clarity When Overwhelmed or Difficult Decision for a bounded structured session.',
          a: 'Each released workflow explains its result, possible context, process, control boundary, and safety limit before it begins. A programmatic planner moves through four workflow-specific checkpoints and then prepares a named result. Workflow state stays in the current session and cannot change records.'
        },
        {
          q: 'Understand the coaching boundary',
          summary: 'AI Coach supports coaching and experimentation, not therapy or crisis care.',
          a: "If you're dealing with depression, anxiety, trauma, and other mental health concerns please reach out to a qualified professional. If you feel at risk or in crisis, contact local emergency or crisis services. AI Coach is coaching, not therapy."
        },
        {
          q: 'Keep Guided Coaching under your control',
          summary: 'The Coach structures reflection but does not make the decision or change records.',
          a: 'You choose the goal, context, and decision. End the guided session at any time. Use the complete manual Observe, Test, Think, Journal, Reflect, Decide, and Results workflows whenever you prefer.'
        }
      ]
    },
    {
      title: 'Community',
      icon: <Icon name="users" size={18} strokeWidth={2} />,
	      items: [
	        {
	          q: 'Use ThryveLoop Network',
	          summary: 'Open Community to inspect relationships across your loop.',
	          a: 'The network graph links Observe, Test, Decide, Journal, Think, AI Coach, and Community using your own records.'
	        },
	        {
	          q: 'Drill into community themes',
	          summary: 'Switch between peer matching, mentorship, collaboration, and engagement.',
	          a: 'Theme tabs focus the Community node, update graph weights, and show matching profile signals and strategy records.'
	        }
	      ]
	    }
	  ];

  const normalizedHelpQuery = helpQuery.trim().toLowerCase();
  const filteredFaqSections = normalizedHelpQuery
    ? faqSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => (
          [section.title, item.q, item.summary, item.a]
            .join(' ')
            .toLowerCase()
            .includes(normalizedHelpQuery)
        ))
      }))
      .filter((section) => section.items.length > 0)
    : faqSections;

  const sectionIdForTitle = (title) => ({
    'Safe Web & Desktop Setup': 'safe-web-desktop-setup',
    'Use the Loop': 'use-the-loop',
    'Sync & Privacy': 'sync-privacy',
    Plans: 'plans',
    'AI Coach': 'ai-coach'
  }[title]);

  return (
    <div className="page-container public-help-page">
      <PublicPageHeader
        onNavigateHome={handleHomeClick}
        onNavigatePage={onNavigatePage}
        activePage={activePage}
        compact
        backLabel="Back home"
      />

      <section className="page-hero-compact">
        <ScrollReveal animation="fade-up">
          <div className="hero-content">
            <h1 className="page-title-display">{pageHeading}</h1>
            <p className="hero-subtitle page-subtitle-compact">
              {pageSubtitle}
            </p>
          </div>
        </ScrollReveal>
      </section>

      <section className="page-section page-section-tight">
        <div className="section-content page-content-narrow">
          {isResourcesPage && (
            <>
              <ScrollReveal animation="fade-up">
            <div className="resource-search-shell" aria-label="Search all resources">
              <div className="resource-search-toolbar">
                <label className="resource-control resource-search-control">
                  <span className="resource-control-label">Search</span>
                  <span className="resource-search-input-wrap">
                    <Icon name="search" size={19} />
                    <input
                      type="search"
                      placeholder="Search guidance, research, policies..."
                      value={resourceQuery}
                      onChange={(event) => {
                        setResourceQuery(event.target.value);
                        setSelectedResourceId(null);
                      }}
                    />
                  </span>
                </label>

                <label className="resource-control" htmlFor="resource-type-filter">
                  <span className="resource-control-label">Type</span>
                  <select
                    id="resource-type-filter"
                    value={resourceType}
                    onChange={(event) => {
                      setResourceType(event.target.value);
                      setSelectedResourceId(null);
                    }}
                    className="resource-search-select"
                  >
                    {RESOURCE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type === 'All' ? 'All resources' : type}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="resource-control" htmlFor="resource-sort-filter">
                  <span className="resource-control-label">Sort</span>
                  <select
                    id="resource-sort-filter"
                    value={resourceSort}
                    onChange={(event) => setResourceSort(event.target.value)}
                    className="resource-search-select"
                  >
                    <option value="newest">Newest first</option>
                    <option value="oldest">Oldest first</option>
                    <option value="title">Title A–Z</option>
                  </select>
                </label>

                <div className="resource-toolbar-actions">
                  <div className="resource-view-toggle" aria-label="Resource view mode">
                    <button
                      type="button"
                      className={resourceView === 'list' ? 'active' : ''}
                      onClick={() => setResourceView('list')}
                      aria-pressed={resourceView === 'list'}
                      aria-label="List view"
                      title="List view"
                    >
                      <Icon name="list-checks" size={18} />
                      <span>List</span>
                    </button>
                    <button
                      type="button"
                      className={resourceView === 'graph' ? 'active' : ''}
                      onClick={() => setResourceView('graph')}
                      aria-pressed={resourceView === 'graph'}
                      aria-label="Graph view"
                      title="Graph view"
                    >
                      <Icon name="git-merge" size={18} />
                      <span>Graph</span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="resource-results-summary" aria-live="polite">
                <span><strong>{filteredResources.length}</strong> {filteredResources.length === 1 ? 'resource' : 'resources'}</span>
                <span className="resource-results-actions">
                  {(resourceQuery || resourceType !== 'All' || resourceSort !== 'newest') && (
                    <button type="button" onClick={resetResourceFilters}>
                      <Icon name="x" size={15} />
                      Clear filters
                    </button>
                  )}
                  <a href="/rss.xml" className="resource-rss-link" aria-label="Open the Resources RSS feed">
                    <Icon name="rss" size={16} />
                    RSS feed
                  </a>
                </span>
              </div>

              {resourceView === 'graph' ? (
                <div className="resource-graph-view" aria-label="Resource relationship graph">
                  {graphResources.length > 0 ? (
                    <div className="resource-graph-layout">
                      <div className="resource-graph-canvas">
                        <div className="resource-graph-topline">
                          <span>Knowledge graph</span>
                          {selectedResource && (
                            <button type="button" onClick={() => setSelectedResourceId(null)}>
                              Full graph
                            </button>
                          )}
                        </div>

                        <svg viewBox="0 0 100 100" role="presentation" aria-hidden="true">
                          {graphResources.flatMap((item, index) => {
                            const source = graphNodePosition(index, graphResources.length);
                            return (item.links || [])
                              .map((targetId) => graphResources.findIndex((candidate) => candidate.id === targetId))
                              .filter((targetIndex) => targetIndex >= 0 && targetIndex > index)
                              .map((targetIndex) => {
                                const target = graphNodePosition(targetIndex, graphResources.length);
                                return (
                                  <line
                                    key={`${item.id}-${graphResources[targetIndex].id}`}
                                    x1={source.x}
                                    y1={source.y}
                                    x2={target.x}
                                    y2={target.y}
                                  />
                                );
                              });
                          })}
                        </svg>

                        {graphResources.map((item, index) => {
                          const position = graphNodePosition(index, graphResources.length);
                          const isActive = graphActiveResource?.id === item.id;
                          return (
                            <button
                              type="button"
                              key={item.id}
                              className={`resource-graph-node resource-graph-node-${item.type.toLowerCase()}${isActive ? ' active' : ''}`}
                              style={{ left: `${position.x}%`, top: `${position.y}%` }}
                              onClick={() => setSelectedResourceId(item.id)}
                              aria-pressed={selectedResourceId === item.id}
                              aria-label={`Focus ${item.title}: ${item.summary}`}
                            >
                              <span className="resource-graph-node-dot" aria-hidden="true" />
                              <span className="resource-graph-node-label">{item.title}</span>
                            </button>
                          );
                        })}
                      </div>

                      {graphActiveResource && (
                        <aside className="resource-graph-detail" aria-live="polite">
                          <span className="resource-graph-detail-type">{graphActiveResource.type}</span>
                          <div>
                            <p>{selectedResource ? 'Focused resource' : 'Graph starting point'}</p>
                            <h2>{graphActiveResource.title}</h2>
                          </div>
                          <p>{graphActiveResource.summary}</p>
                          <div className="resource-graph-tags" aria-label="Resource topics">
                            {graphActiveResource.tags.map((tag) => <span key={tag}>{tag}</span>)}
                          </div>
                          <a href={graphActiveResource.href} className="resource-graph-open-link">
                            Open resource
                            <Icon name="arrow-right" size={17} />
                          </a>
                          <div className="resource-graph-related">
                            <strong>{graphRelatedResources.length} linked {graphRelatedResources.length === 1 ? 'resource' : 'resources'}</strong>
                            {graphRelatedResources.slice(0, 4).map((item) => (
                              <button type="button" key={item.id} onClick={() => setSelectedResourceId(item.id)}>
                                <span>{item.title}</span>
                                <Icon name="chevron-right" size={15} />
                              </button>
                            ))}
                          </div>
                        </aside>
                      )}
                    </div>
                  ) : (
                    <div className="resource-empty-state">
                      <strong>No resources match this search.</strong>
                      <span>Clear the filters to rebuild the graph.</span>
                    </div>
                  )}
                  <p className="resource-graph-caption">
                    Select a node to focus its direct connections. Use the detail panel to open a resource or continue through related topics.
                  </p>
                </div>
              ) : (
                <div className="resource-list-view" aria-live="polite">
                  {filteredResources.map((item) => (
                    <a className="resource-result-row" href={item.href} key={item.id}>
                      <span className="resource-result-visual">{item.type}</span>
                      <span className="resource-result-copy">
                        <span className="resource-result-meta">{item.type} · {new Date(`${item.date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <strong>{item.title}</strong>
                        <span>{item.summary}</span>
                      </span>
                      <Icon name="arrow-right" size={22} />
                    </a>
                  ))}
                  {filteredResources.length === 0 && (
                    <div className="resource-empty-state">
                      <strong>No resources match this search.</strong>
                      <span>Try a broader keyword or choose all resource types.</span>
                    </div>
                  )}
                </div>
              )}
            </div>
              </ScrollReveal>

              <aside className="agent-resource-panel" aria-label="Agent resources">
                <h2>AGENTS</h2>
                <div className="agent-resource-links">
                  {AGENT_RESOURCES.map((item) => (
                    <a href={item.href} key={item.href}>
                      <strong>{item.label}</strong>
                      <span>{item.summary}</span>
                    </a>
                  ))}
                </div>
              </aside>
            </>
          )}

          {!isResourcesPage && (
            <>
              <ScrollReveal animation="fade-up">
                <div className="public-help-start-panel">
                  <div>
                    <span className="public-help-release">Current guidance · Release {CURRENT_RELEASE_VERSION}</span>
                    <h2>What do you need help with?</h2>
                    <p>Choose a task for the shortest route to the right steps.</p>
                  </div>
                  <a className="public-help-support-link" href={`mailto:${HELP_TRUST_FACTS.supportEmail}`}>
                    <Icon name="message-circle" size={18} />
                    Email human support
                  </a>
                </div>
              </ScrollReveal>

              <ScrollReveal animation="fade-up">
                <nav className="public-help-task-grid" aria-label="Help by task">
                  {HELP_TASKS.map((task) => (
                    <a className="public-help-task" href={`#${task.publicSection}`} key={task.id}>
                      <span aria-hidden="true"><Icon name={task.icon} size={19} /></span>
                      <div>
                        <h2>{task.title}</h2>
                        <p>{task.summary}</p>
                      </div>
                      <Icon name="arrow-right" size={18} />
                    </a>
                  ))}
                </nav>
              </ScrollReveal>

              <ScrollReveal animation="fade-up">
                <section className="public-help-platform-section" aria-labelledby="platform-help-title">
                  <div className="public-help-section-heading">
                    <span>Choose your platform</span>
                    <h2 id="platform-help-title">Web, mobile web, and Desktop behave differently where it matters.</h2>
                  </div>
                  <div className="public-help-platform-grid">
                    {PLATFORM_GUIDANCE.map((platform) => (
                      <article className="public-help-platform" key={platform.id}>
                        <span aria-hidden="true"><Icon name={platform.icon} size={19} /></span>
                        <small>{platform.eyebrow}</small>
                        <h3>{platform.title}</h3>
                        <p>{platform.summary}</p>
                        <strong>{platform.action}</strong>
                      </article>
                    ))}
                  </div>
                </section>
              </ScrollReveal>

              <ScrollReveal animation="fade-up">
                <section className="public-help-loop-section" aria-labelledby="help-loop-title">
                  <div className="public-help-section-heading">
                    <span>The complete manual path</span>
                    <h2 id="help-loop-title">Use the loop with or without AI.</h2>
                    <p>{HELP_TRUST_FACTS.manualPath}</p>
                  </div>
                  <div className="public-help-reading-path" aria-label="ThryveLoop stages">
                    {CORE_LOOP_GROUPS.map((item) => (
                      <article className="public-help-path-step" key={item.number}>
                        <span>{item.number}</span>
                        <div>
                          <h2>{item.title}</h2>
                          <p>{item.detail}</p>
                        </div>
                      </article>
                    ))}
                  </div>
                </section>
              </ScrollReveal>

              <ScrollReveal animation="fade-up">
                <label className="public-help-topic-search">
                  <span className="public-help-topic-search-label">Search Help topics</span>
                  <span className="public-help-topic-search-control">
                    <Icon name="search" size={19} />
                    <input
                      type="search"
                      value={helpQuery}
                      onChange={(event) => setHelpQuery(event.target.value)}
                      placeholder="Try sync key, Desktop, AI Coach, billing, or export"
                    />
                  </span>
                </label>
              </ScrollReveal>

              {filteredFaqSections.map((section, sectionIdx) => (
                <div
                  id={sectionIdForTitle(section.title)}
                  key={section.title}
                  className="public-help-topic-section"
                >
                  <ScrollReveal animation="fade-up" delay={sectionIdx * 50}>
                    <div className="public-help-topic-heading">
                      <span>{section.icon}</span>
                      <h2>{section.title}</h2>
                    </div>
                  </ScrollReveal>

                  <StaggerGroup staggerDelay={70}>
                    {section.items.map((item, itemIdx) => (
                      <CollapsibleSection
                        key={item.q}
                        title={item.q}
                        summary={item.summary}
                        variant="default"
                        defaultExpanded={!normalizedHelpQuery && sectionIdx === 0 && itemIdx === 0}
                      >
                        <p className="page-muted-copy public-help-answer">
                          {item.a}
                        </p>
                      </CollapsibleSection>
                    ))}
                  </StaggerGroup>
                </div>
              ))}

              {filteredFaqSections.length === 0 && (
                <div className="public-help-empty" role="status">
                  <Icon name="search-x" size={24} />
                  <strong>No Help topic matches “{helpQuery}”.</strong>
                  <span>Try a shorter phrase or email {HELP_TRUST_FACTS.supportEmail}.</span>
                </div>
              )}

              <ScrollReveal animation="fade-up">
                <div id="contact-support" className="public-help-contact-panel">
                  <div>
                    <span>Human support</span>
                    <h2>Still stuck? Tell us what you expected and what happened.</h2>
                    <p>
                      Use the feedback link in Settings or email{' '}
                      <a href={`mailto:${HELP_TRUST_FACTS.supportEmail}`}>
                        {HELP_TRUST_FACTS.supportEmail}
                      </a>.
                    </p>
                  </div>
                  <div className="public-help-contact-actions">
                    <a className="page-primary-cta" href={`mailto:${HELP_TRUST_FACTS.supportEmail}`}>Send a message</a>
                    {onStartApp && (
                      <button onClick={handleStartApp} className="page-secondary-cta" type="button">
                        Start free on Web
                      </button>
                    )}
                  </div>
                </div>
              </ScrollReveal>
            </>
          )}
        </div>
      </section>

      <PublicPageFooter
        activePage={activePage}
        onNavigateHome={handleHomeClick}
        onNavigatePage={onNavigatePage}
      />
    </div>
  );
};

export default HelpPage;
